<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :title="itemData.name"
    class="element_card relative block w-full cursor-pointer overflow-hidden rounded shadow-normal shadow-gray-400 hover:shadow-blue-400/50 tablet:flex"
    @click="handleClick"
  >
    <div
      class="flex w-full flex-col justify-between p-3 tablet:h-[320px] tablet:w-6/12 tablet:p-5 desktop:h-[450px]"
    >
      <div>
        <h3 class="mb-2 text-base font-bold tablet:line-clamp-2 tablet:text-lg">
          {{ itemData.name }}
        </h3>
        <p
          class="mb-2 text-sm text-gray-600 tablet:line-clamp-4 tablet:text-base"
          v-html="provideSanitized(decode(itemData.description))"
        />
      </div>
      <div>
        <div class="flex items-center">
          <div
            v-if="itemData.customerLogo"
            class="mr-5 w-[78px] basis-[78px] desktop:w-[160px] desktop:basis-[160px]"
          >
            <figure
              class="relative aspect-video w-full overflow-hidden rounded"
            >
              <img
                :src="provideImageUrl(itemData.customerLogo)"
                :alt="
                  itemData.customerName || itemData.name || itemData.description
                "
                loading="lazy"
                class="pointer-events-none absolute left-0 top-0 block h-full w-full object-contain"
              />
            </figure>
          </div>
          <div class="flex-1 text-sm desktop:text-base">
            <p
              v-if="itemData.customerName"
              class="line-clamp-1 font-bold tablet:mb-1"
            >
              {{ itemData.customerName }}
            </p>
            <p v-if="itemData.region" class="line-clamp-1">
              <strong
                class="mr-3 after:pl-3 after:font-normal after:content-['|']"
              >
                {{ t('region') }}
              </strong>
              {{ itemData.region }}
            </p>
          </div>
        </div>
        <div class="hidden justify-end tablet:flex">
          <TheNuxtLink
            :to="provideFormatUrl(itemData.link)"
            :title="itemData.name"
            class="element_arrow flex h-8 w-8 basis-8 items-center justify-center rounded text-blue-400"
          >
            <IconArrow class="h-5 w-5" direction="right" />
          </TheNuxtLink>
        </div>
      </div>
    </div>
    <div
      class="relative flex min-h-[200px] w-full items-center overflow-hidden p-3 tablet:h-[320px] tablet:min-h-0 tablet:w-6/12 tablet:p-5 desktop:h-[450px]"
    >
      <figure
        class="absolute left-0 top-0 h-full w-full overflow-hidden"
        :class="
          hasThumbnailMask &&
          'after:absolute after:inset-0 after:block after:bg-gradient-to-b after:from-black/80 after:to-black/50 after:content-[\'\']'
        "
      >
        <img
          :src="provideImageUrl(itemData.thumbnail)"
          :alt="itemData.name || itemData.description"
          loading="lazy"
          class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
        />
      </figure>
      <div class="relative">
        <template v-if="itemData.recommended">
          <IconComment class="mb-3 text-white" />
          <p
            class="mb-3 text-base text-white tablet:line-clamp-5 tablet:text-lg"
          >
            {{ itemData.recommended }}
          </p>
        </template>
        <div class="flex tablet:items-center">
          <figure
            v-if="itemData.authorIcon"
            class="h-11 w-11 basis-11 overflow-hidden rounded-full desktop:h-[60px] desktop:w-[60px] desktop:basis-[60px]"
          >
            <img
              :src="provideImageUrl(itemData.authorIcon)"
              :alt="
                itemData.authorName || itemData.name || itemData.description
              "
              loading="lazy"
              class="pointer-events-none block h-full w-full object-cover"
            />
          </figure>
          <div class="flex-1 pl-2 text-sm text-white desktop:pl-5">
            <p v-if="itemData.authorName" class="mb-1 line-clamp-1">
              {{ itemData.authorName }}
            </p>
            <p v-if="itemData.authorJobTitle" class="line-clamp-1">
              {{ itemData.authorJobTitle }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <TheNuxtLink
      v-if="isCN"
      :id="itemData.trackingCode"
      :title="itemData.name"
      :to="provideFormatUrl(itemData.link)"
      class="absolute left-0 top-0 block h-full w-full"
    />
  </div>
</template>

<script setup lang="ts">
  import { decode } from 'entities';

  import type { ISuccessfulCaseTab } from '@/components/Shared/Suite/SuccessfulCase/index.vue';
  import type { TUrlConnector } from '@/components/Shared/Suite/type.ts';

  interface IProps {
    itemData: ISuccessfulCaseTab;
    urlConnector?: TUrlConnector;
  }

  const props = defineProps<IProps>();

  const { t } = useI18n({ useScope: 'local' });
  const { isCN } = useLanguage();
  const { provideFormatUrl, processLink } = useFormatUrl();
  const { provideResponsiveImgUrl } = useSolutionsData();

  // 只要圖片那一塊的內容有值，就會套用漸層遮罩，避免圖片上的文字被圖片吃掉的情況

  const hasThumbnailMask = computed((): boolean => {
    const { itemData } = props;
    const { recommended, authorIcon, authorName, authorJobTitle } = itemData;
    const hasContent =
      recommended || authorIcon || authorName || authorJobTitle;
    return !!hasContent;
  });

  const provideImageUrl = (name: string): string => {
    if (!name) return '';

    if (props.urlConnector === 'solution') {
      return provideResponsiveImgUrl(name);
    }
    return name;
  };

  const handleClick = (): void => {
    if (isCN.value) return;
    processLink(provideFormatUrl(props.itemData.link));
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "region": "Region"
  },
  "en-jp": {
    "region": "Region"
  },
  "en-tw": {
    "region": "Region"
  },
  "en-kr": {
    "region": "Region"
  },
  "en-americas": {
    "region": "Region"
  },
  "en-asia-pacific": {
    "region": "Region"
  },
  "en-emea": {
    "region": "Region"
  },
  "ja-jp": {
    "region": "地域"
  },
  "zh-cn": {
    "region": "区域"
  },
  "zh-tw": {
    "region": "區域"
  }
}
</i18n>

<style lang="scss" scoped>
  .element_card:hover {
    .element_arrow {
      @apply bg-blue-400 text-white;
    }
  }
</style>
